import { useState, useEffect, useCallback } from "react";
import { Authenticated, Unauthenticated, AuthLoading } from "convex/react";
import { Routes, Route } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import Index from "../pages/Index";
import NotFound from "../pages/NotFound";
import { AuthenticationPage } from "./AuthenticationPage";
import { SuperAdminAuthWrapper } from "./SuperAdminAuthWrapper";
import { Button } from "./ui/button";
import { setSelectedDeployment, getSelectedDeploymentId, getSelectedDeployment } from "@/config/deployments";
import shomriLogo from "@/assets/shomri-logo-new.png";
import CounterLoading from "./ui/counter-loader";
import { AlertTriangle, RefreshCw, Trash2, ExternalLink } from "lucide-react";

type AuthErrorType = "domain_not_configured" | "unknown_error" | null;

interface ClerkError {
  code?: string;
  message?: string;
}

const LoadingScreen = () => {
  const [showSignOut, setShowSignOut] = useState(false);
  const [authError, setAuthError] = useState<AuthErrorType>(null);
  const [errorDetails, setErrorDetails] = useState<string>("");
  const { signOut } = useClerk();
  const currentDeployment = getSelectedDeploymentId();
  const deployment = getSelectedDeployment();

  // Check for Clerk domain configuration errors
  const checkForClerkErrors = useCallback(async () => {
    try {
      // Attempt to detect domain configuration issues by checking for failed Clerk requests
      const currentDomain = window.location.hostname;
      
      // Check if we're on a domain that might not be configured in Clerk
      if (currentDomain.includes("lovableproject.com") || currentDomain.includes("lovable.app")) {
        // Listen for network errors related to Clerk
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
          const response = await originalFetch(...args);
          const url = args[0]?.toString() || "";
          
          if (url.includes("clerk.accounts.dev") && !response.ok) {
            try {
              const clonedResponse = response.clone();
              const data = await clonedResponse.json();
              if (data.errors?.[0]?.code === "dev_browser_unauthenticated") {
                setAuthError("domain_not_configured");
                setErrorDetails(`Domain "${currentDomain}" may not be configured in Clerk dashboard.`);
              }
            } catch {
              // Ignore JSON parse errors
            }
          }
          return response;
        };
      }
    } catch (error) {
      console.error("Error checking Clerk configuration:", error);
    }
  }, []);

  useEffect(() => {
    checkForClerkErrors();
    
    const timer = setTimeout(() => {
      setShowSignOut(true);
    }, 5000);
    
    // After 8 seconds, if still loading, check for common issues
    const errorCheckTimer = setTimeout(() => {
      if (!authError) {
        setAuthError("unknown_error");
        setErrorDetails("Authentication is taking longer than expected. This may be a domain configuration issue.");
      }
    }, 8000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(errorCheckTimer);
    };
  }, [checkForClerkErrors, authError]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out failed:", error);
    }
    window.location.reload();
  };

  const handleSwitchToDev = () => {
    setSelectedDeployment("dev");
    window.location.reload();
  };

  const handleClearCookies = () => {
    // Clear all cookies for the current domain
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    // Clear localStorage
    localStorage.clear();
    // Reload the page
    window.location.reload();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Error state UI
  if (authError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Authentication Issue</h2>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            {authError === "domain_not_configured" 
              ? "The current domain is not configured in the Clerk dashboard for this deployment."
              : "There was an issue connecting to the authentication service."}
          </p>
          
          {errorDetails && (
            <div className="bg-muted/50 rounded-md p-3 mb-4">
              <p className="text-xs font-mono text-muted-foreground break-all">{errorDetails}</p>
            </div>
          )}
          
          <div className="space-y-3 mb-4">
            <p className="text-sm font-medium text-foreground">Try these solutions:</p>
            <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
              <li>Clear browser cookies and reload</li>
              <li>Add <code className="bg-muted px-1 rounded text-xs">{window.location.hostname}</code> to Clerk's allowed domains</li>
              <li>Switch to a different deployment environment</li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button onClick={handleClearCookies} variant="default" className="w-full gap-2">
              <Trash2 className="h-4 w-4" />
              Clear Cookies & Reload
            </Button>
            <Button onClick={handleRefresh} variant="outline" className="w-full gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh Page
            </Button>
            {currentDeployment !== "dev" && (
              <Button onClick={handleSwitchToDev} variant="ghost" className="w-full">
                Switch to Development
              </Button>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Current deployment: <span className="font-medium">{deployment.name}</span> ({deployment.environment})
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background">
      <CounterLoading />
      <img src={shomriLogo} alt="Shomri Logo" className="w-12 h-12 object-contain mt-8 mb-2" />
      <p className="text-muted-foreground">Loading...</p>
      {showSignOut && (
        <div className="mt-6 space-y-2 text-center">
          <p className="text-sm text-muted-foreground mb-2">Taking too long?</p>
          <div className="flex flex-col gap-2">
            <Button variant="outline" size="sm" onClick={handleClearCookies} className="gap-2">
              <Trash2 className="h-3 w-3" />
              Clear Cookies & Reload
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Sign Out
            </Button>
            {currentDeployment !== "dev" && (
              <Button variant="ghost" size="sm" onClick={handleSwitchToDev}>
                Switch to Development
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AuthenticatedApp = () => {
  return (
    <>
      <AuthLoading>
        <LoadingScreen />
      </AuthLoading>
      
      <Unauthenticated>
        <AuthenticationPage />
      </Unauthenticated>
      
      <Authenticated>
        <SuperAdminAuthWrapper>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/section/:sectionId" element={<Index />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </SuperAdminAuthWrapper>
      </Authenticated>
    </>
  );
};